import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy .env.example to .env.local.",
  );
}

export const supabase = createClient(url, anonKey);
export const FUNCTIONS_URL = `${url}/functions/v1`;

/**
 * An Edge Function said no, with the machine-readable half of why still on it.
 *
 * A bare Error would throw away the PT402 that tells the caller this was a plan
 * boundary rather than a fault — the same signal `asAppError` preserves for a
 * PostgREST refusal, and for the same reason: string-matching an English
 * sentence to decide whether to show an upgrade prompt breaks the first time
 * the sentence is reworded (ADR-0009).
 *
 * Defined here rather than in entitlements.ts because that module imports this
 * one, and a cycle between them would be a worse trade than a small class.
 */
export class FunctionError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly details: string | null,
  ) {
    super(message);
    this.name = "FunctionError";
  }
}

/** Call an Edge Function with the caller's session attached. */
export async function callFunction<T>(name: string, body: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");

  const res = await fetch(`${FUNCTIONS_URL}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new FunctionError(
      payload.error ?? `${name} failed (${res.status})`,
      res.status,
      payload.code ?? null,
      payload.details ?? null,
    );
  }
  return payload as T;
}
